/**
 * 应用更新状态管理 — Zustand Store
 * 集中管理更新检查、下载、安装等全流程状态，供 App 根级别和 MainLayout 共同消费。
 */

import { create } from 'zustand';
import type { AppUpdateStatusPayload } from '../../common/types/appUpdate';

type UpdateCheckAuthority = 'unknown' | 'has-update' | 'no-update';

/** handleUpdateStatusEvent 返回的副作用描述，由调用方（hook）执行 */
export interface UpdateStatusSideEffects {
  /** 需要上报弹窗曝光 */
  shouldReportExpose: boolean;
  /** 需要展示"当前已是最新版本"的 toast */
  shouldShowNotAvailableToast: boolean;
  /** 需要展示 error toast，内容为此字段值 */
  errorToastMessage: string;
}

interface AppUpdateState {
  pendingUpdateStatus: AppUpdateStatusPayload | null;
  availableUpdateStatus: AppUpdateStatusPayload | null;
  downloadingUpdateStatus: AppUpdateStatusPayload | null;
  installingUpdateStatus: AppUpdateStatusPayload | null;
  updateCheckAuthority: UpdateCheckAuthority;
  updateModalOpen: boolean;
  updateActionSubmitting: boolean;
  manualUpdateChecking: boolean;
  forceUpdateFlowActive: boolean;
  forceUpdateFlowError: string;
}

interface AppUpdateActions {
  setPendingUpdateStatus: (v: AppUpdateStatusPayload | null) => void;
  setAvailableUpdateStatus: (v: AppUpdateStatusPayload | null) => void;
  setDownloadingUpdateStatus: (v: AppUpdateStatusPayload | null) => void;
  setInstallingUpdateStatus: (v: AppUpdateStatusPayload | null) => void;
  setUpdateCheckAuthority: (v: UpdateCheckAuthority) => void;
  setUpdateModalOpen: (v: boolean) => void;
  setUpdateActionSubmitting: (v: boolean) => void;
  setManualUpdateChecking: (v: boolean) => void;
  setForceUpdateFlowActive: (v: boolean) => void;
  setForceUpdateFlowError: (v: string) => void;
  /** 处理主进程推送的 UPDATE_STATUS 事件，返回需要执行的副作用描述 */
  handleUpdateStatusEvent: (payload: AppUpdateStatusPayload) => UpdateStatusSideEffects;
}

export type AppUpdateStore = AppUpdateState & AppUpdateActions;

const noSideEffects: UpdateStatusSideEffects = {
  shouldReportExpose: false,
  shouldShowNotAvailableToast: false,
  errorToastMessage: '',
};

export const useAppUpdateStore = create<AppUpdateStore>((set, get) => ({
  // ---- 初始状态 ----
  pendingUpdateStatus: null,
  availableUpdateStatus: null,
  downloadingUpdateStatus: null,
  installingUpdateStatus: null,
  updateCheckAuthority: 'unknown',
  updateModalOpen: false,
  updateActionSubmitting: false,
  manualUpdateChecking: false,
  forceUpdateFlowActive: false,
  forceUpdateFlowError: '',

  // ---- 独立 setter ----
  setPendingUpdateStatus: (v) => set({ pendingUpdateStatus: v }),
  setAvailableUpdateStatus: (v) => set({ availableUpdateStatus: v }),
  setDownloadingUpdateStatus: (v) => set({ downloadingUpdateStatus: v }),
  setInstallingUpdateStatus: (v) => set({ installingUpdateStatus: v }),
  setUpdateCheckAuthority: (v) => set({ updateCheckAuthority: v }),
  setUpdateModalOpen: (v) => set({ updateModalOpen: v }),
  setUpdateActionSubmitting: (v) => set({ updateActionSubmitting: v }),
  setManualUpdateChecking: (v) => set({ manualUpdateChecking: v }),
  setForceUpdateFlowActive: (v) => set({ forceUpdateFlowActive: v }),
  setForceUpdateFlowError: (v) => set({ forceUpdateFlowError: v }),

  // ---- 核心事件处理（纯状态更新 + 副作用描述） ----
  handleUpdateStatusEvent: (nextStatus) => {
    const state = get();

    if (nextStatus.status === 'available') {
      const isForce = nextStatus.forceUpdate === true;
      set({
        updateCheckAuthority: 'has-update',
        manualUpdateChecking: false,
        pendingUpdateStatus: null,
        availableUpdateStatus: nextStatus,
        downloadingUpdateStatus: null,
        installingUpdateStatus: null,
        // 非强制：弹常规更新弹窗；强制：激活蒙层流程（蒙层中展示更新按钮）
        updateModalOpen: !isForce,
        updateActionSubmitting: false,
        forceUpdateFlowError: '',
        ...(isForce ? { forceUpdateFlowActive: true } : {}),
      });
      return { ...noSideEffects, shouldReportExpose: true };
    }

    if (nextStatus.status === 'downloading') {
      set({
        updateCheckAuthority: 'has-update',
        manualUpdateChecking: false,
        pendingUpdateStatus: null,
        downloadingUpdateStatus: nextStatus,
        installingUpdateStatus: null,
        updateActionSubmitting: false,
        ...(nextStatus.forceUpdate ? { forceUpdateFlowActive: true, forceUpdateFlowError: '' } : {}),
      });
      return { ...noSideEffects };
    }

    if (nextStatus.status === 'downloaded') {
      set({
        updateCheckAuthority: 'has-update',
        manualUpdateChecking: false,
        pendingUpdateStatus: nextStatus,
        availableUpdateStatus: null,
        downloadingUpdateStatus: null,
        installingUpdateStatus: null,
        updateActionSubmitting: false,
        ...(nextStatus.forceUpdate ? { forceUpdateFlowActive: true, forceUpdateFlowError: '' } : {}),
      });
      return { ...noSideEffects };
    }

    if (nextStatus.status === 'installing') {
      const shouldActivateForce = nextStatus.forceUpdate || state.forceUpdateFlowActive;
      set({
        updateCheckAuthority: 'has-update',
        manualUpdateChecking: false,
        pendingUpdateStatus: null,
        availableUpdateStatus: null,
        downloadingUpdateStatus: null,
        installingUpdateStatus: nextStatus,
        updateActionSubmitting: false,
        updateModalOpen: false,
        forceUpdateFlowError: '',
        ...(shouldActivateForce ? { forceUpdateFlowActive: true } : {}),
      });
      return { ...noSideEffects };
    }

    if (nextStatus.status === 'not-available') {
      const wasManualChecking = state.manualUpdateChecking;
      set({
        updateCheckAuthority: 'no-update',
        manualUpdateChecking: false,
        availableUpdateStatus: null,
        downloadingUpdateStatus: null,
        installingUpdateStatus: null,
        updateModalOpen: false,
        updateActionSubmitting: false,
        forceUpdateFlowActive: false,
        forceUpdateFlowError: '',
      });
      return { ...noSideEffects, shouldShowNotAvailableToast: wasManualChecking };
    }

    if (nextStatus.status === 'error') {
      set({
        manualUpdateChecking: false,
        downloadingUpdateStatus: null,
        installingUpdateStatus: null,
        updateActionSubmitting: false,
      });
      const errorMsg = nextStatus.errorMessage || '';
      if (errorMsg && state.forceUpdateFlowActive) {
        set({ forceUpdateFlowError: errorMsg });
      }
      return { ...noSideEffects, errorToastMessage: errorMsg };
    }

    return { ...noSideEffects };
  },
}));
